import type { Ref } from 'react'
import { forwardRef, useImperativeHandle, useState, useEffect } from 'react'

import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark'
import CustomButton from '@space-lib/components/customButton'
import Icon from '@space-lib/components/icon'
import Loader from '@space-lib/components/loader'
import Modal from '@space-lib/components/modal'
import { VariationModal } from '@space-lib/components/modal/enum'
import ModalHeader from '@space-lib/components/modalHeader'
import { Actions, Entities } from '@space-lib/datahub'
import type { ShowcaseOrientation } from '@space-lib/enum/showcase'
import { useManageModal } from '@space-lib/hooks'
import useTranslation from '@space-lib/hooks/useTranslation'
import SearchError from '@space-shell/assets/images/errorSearch.svg'
import { useDataHubEvents } from '@space-shell/common/hooks/useDataHubEvents'
import { useGoogleAnalytics } from '@space-shell/common/hooks/useGoogleAnalytics'
import { EntityType, type Result } from '@space-shell/common/services/globalSearch/types'
import { useMembershipStore } from '@space-shell/core/store'
import { secondsModalOpen } from '@space-shell/modules/search/utils/secondsModalOpen'
import { useNavigate, useSearchParams } from 'react-router-dom'

import '@hotmart/cosmos/dist/styles/utilities/position.css'
import ErrorSearch from '../../globalSearch/errorFindMembership'
import Content from '../content'
import Grid from '../grid'
import { CloseButton, ModalBodyCustom } from '../styles'
import type { ResultsModalProps, SearchResultsProps } from '../types'

const SearchResults = (
  {
    isLoading,
    isFetchingNextPage,
    isError,
    results,
    term,
    hasNextPage,
    onTryAgain,
    onLoadNextPage,
    onCloseModal
  }: SearchResultsProps,
  ref: Ref<ResultsModalProps>
) => {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const { isModalOpen, handleOpen, handleClose } = useManageModal()
  const { sendDataHubEvent } = useDataHubEvents()
  const [searchParams] = useSearchParams()
  const { sendGoogleAnalyticsEvent } = useGoogleAnalytics()
  const [durationModalOpened, setDurationModalOpened] = useState<Date>(new Date())
  const { membershipData } = useMembershipStore()

  const queryParam = searchParams.get('q')
  const query = decodeURIComponent(queryParam || '')

  const searchTerm = term || query

  useEffect(() => {
    if (isModalOpen) {
      setDurationModalOpened(new Date())
    }
  }, [isModalOpen])

  const orientation = membershipData?.settings?.orientation?.global

  const redirectToUrl = ({ entityId, entityType, id, membershipCode, parent, hashId }) => {
    const language = String(i18n.language).toLowerCase()

    if (entityType === EntityType.PAGE) {
      window.location.replace(
        `/${language}/club/${membershipCode}/products/${parent?.id}/content/${hashId}?source=GLOBAL_SEARCH`
      )

      return
    }

    if (entityType === EntityType.COMMUNITY) {
      navigate(`/${language}/club/${membershipCode}/${entityType.toLowerCase()}/${id}`)

      return
    }

    if (entityType === EntityType.POST) {
      navigate(
        `/${language}/club/${membershipCode}/community/${id}/${entityType.toLowerCase()}/${entityId}`
      )

      return
    }

    navigate(`/${language}/club/${membershipCode}/products/${id}`)
  }

  const handleClickItem = ({
    entityId,
    entityType,
    id,
    membershipCode,
    name,
    parent,
    hashId
  }: Result) => {
    sendDataHubEvent({
      group: Entities.MODAL_SEARCH,
      name: Actions.CLICK_ITEM_SEARCH_MODAL,
      params: {
        modal: {
          product: {
            id,
            name,
            orientation: orientation as unknown as ShowcaseOrientation,
            type: entityType
          }
        },
        search: {
          term: searchTerm
        }
      }
    })

    sendGoogleAnalyticsEvent({
      group: Entities.MODAL_SEARCH,
      name: Actions.CLICK_ITEM_SEARCH_MODAL,
      label: searchTerm
    })

    handleClose()
    onCloseModal()
    redirectToUrl({ entityId, entityType, id, membershipCode, parent, hashId })
  }

  useImperativeHandle(
    ref,
    () => ({
      show: isModalOpen,
      open: handleOpen,
      close: handleClose
    }),
    [handleClose, handleOpen, isModalOpen]
  )

  const handlerCloseModal = () => {
    handleClose()
    onCloseModal()

    sendDataHubEvent({
      group: Entities.HEADER_SEARCH,
      name: Actions.CLICK_EXIT_SEARCH,
      params: {
        search: { term: searchTerm },
        modal: {
          secondsOpen: secondsModalOpen(durationModalOpened)
        }
      }
    })

    sendGoogleAnalyticsEvent({
      group: Entities.HEADER_SEARCH,
      name: Actions.CLICK_EXIT_SEARCH,
      label: searchTerm
    })
  }

  useEffect(() => {
    if (isError) {
      sendDataHubEvent({
        group: Entities.HEADER_SEARCH,
        name: Actions.SEARCH_ERROR,
        params: {
          search: {
            term: searchTerm
          }
        }
      })

      sendGoogleAnalyticsEvent({
        group: Entities.HEADER_SEARCH,
        name: Actions.SEARCH_ERROR,
        label: searchTerm
      })
    }
  }, [isError, sendDataHubEvent, sendGoogleAnalyticsEvent, searchTerm])

  return (
    <Modal show={isModalOpen} variation={VariationModal.SEARCH} onClose={handlerCloseModal}>
      <ModalHeader>
        <div className='_pb-1 _border-bottom _d-flex _justify-content-between _align-items-center'>
          <h2 className='_m-0 _text-4 _text-gray-700'>{t('search.title', { term: searchTerm })}</h2>

          <CloseButton
            onClick={handlerCloseModal}
            data-test='button-modal-close'
            className='_align-items-center _border-0 _text-5 _text-gray-900 _line-height-sm _cursor-pointer'>
            <Icon icon={faXmark} />
          </CloseButton>
        </div>
      </ModalHeader>
      <ModalBodyCustom>
        {isError && (
          <ErrorSearch
            title={t('search.error_search')}
            illustration={SearchError}
            textButton={t('error_state.text_button')}
            onClick={onTryAgain}
          />
        )}

        {isLoading && (
          <div className='_d-flex _justify-content-center _align-items-center _h-full'>
            <Loader />
          </div>
        )}

        {results && (
          <>
            <Grid>
              <Content products={results} onClick={handleClickItem} />
              {hasNextPage && (
                <CustomButton
                  className='_mt-3'
                  disabled={isFetchingNextPage}
                  onClick={onLoadNextPage}>
                  {t('buttons.load_more')}
                </CustomButton>
              )}
            </Grid>
          </>
        )}
      </ModalBodyCustom>
    </Modal>
  )
}

export default forwardRef(SearchResults)
